<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import AgentsRecommendation from '@/components/common/agents-recommendation.vue';
import FeaturedArticleList from '@/components/common/featured-article-list.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import IndustryNewsPreview from '@/components/home/industry-news-preview.vue';
import Insights from '@/components/home/insights/insights.vue';
import MobileAppInfo from '@/components/home/mobile-app-info.vue';
import PersonalNewsPreviewMultipleExecution from '@/components/home/personal-news-multiple-execution.vue';
import PickedUpFeeds from '@/components/home/picked-up-feeds.vue';
import ThemeFavoriteBoard from '@/components/home/theme-favorite-board.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { ThemeList } from '@/types';
import { useWebPush } from '@/utils/composables/useWebPush';
import {
  STATES,
  useTeamFeaturedArticles,
  useThemeList,
  useUserInfo,
} from '@/utils/swr';
import { userSession } from '@/utils/userSession';
import { useEmitter, useStore } from '@/utils/vue';

const showAgentsRecommendation = ref(false);

const store = useStore();
const emitter = useEmitter();
const route = useRoute();
const { createSnackbar } = useSnackbar();
const layoutVariant = 'legacy';

const feedType = computed(() => store.state.feedType.feedType);
const { data: userInfo, state: userState } = useUserInfo();
const { data: themes, state: themesState } = useThemeList(feedType);
const { requestWebPushPermissionOnHomeVisit } = useWebPush();

const isLoaded = computed(
  () =>
    userState.value === STATES.SUCCESS && themesState.value === STATES.SUCCESS,
);

onMounted(async () => {
  api.trackPageView({
    pageName: 'home',
    pageUrl: route.fullPath,
    layoutVariant,
  });
  // NOTE: 新着マークニュース削除の対応として一時的に追加している
  userSession.clearTeamMarkSort();

  await requestWebPushPermissionOnHomeVisit(createSnackbar);
});

const { data: featuredArticles, mutate: updateTeamFeaturedArticles } =
  useTeamFeaturedArticles(ref('mark'), ref(1), 8);

const visibleFeaturedArticles = computed(
  () => featuredArticles.value?.featured_articles ?? [],
);

const { setRef: setFeaturedArticlesRef, requestId: featuredArticlesRequestId } =
  useHomeSectionViewTracking(
    'team_feature_articles_view',
    () => featuredArticles.value !== undefined,
    () => ({
      document_count: visibleFeaturedArticles.value.length,
      layout_variant: layoutVariant,
    }),
  );

const activeThemes = computed(() =>
  (themes.value?.themes ?? []).filter(t => !t.is_deleted),
);

const hasThemes = computed(() => activeThemes.value.length > 0);

const showThemeSearchBoard = computed(
  () => activeThemes.value.filter(t => t.is_member).length === 0,
);
const showThemeFavoriteBoard = computed(
  () =>
    activeThemes.value.filter(t => t.is_member).length > 0 &&
    (activeThemes.value.filter(t => t.is_favorite) ?? []).length < 1,
);
const hasFavoriteThemes = computed(
  () => (activeThemes.value.filter(t => t.is_favorite) ?? []).length > 0,
);

emitter.on('article-updated', () => {
  updateTeamFeaturedArticles();
});

const toggleFavorite = async (theme: ThemeList) => {
  if (theme.is_favorite) {
    try {
      await api.deleteFavoriteTheme(theme.id);
      createSnackbar({
        message: 'お気に入りを解除しました',
        type: 'success',
      });
    } catch (error) {
      createSnackbar({
        message: 'お気に入りの解除に失敗しました',
        type: 'error',
      });
      throw error;
    }
  } else {
    try {
      await api.postFavoriteTheme(theme.id);
      createSnackbar({
        message: 'お気に入りに登録しました',
        type: 'success',
      });
    } catch (error) {
      createSnackbar({
        message: 'お気に入りの登録に失敗しました',
        type: 'error',
      });
      throw error;
    }
  }
  const tmpTheme = activeThemes.value.find(
    tmpTheme => tmpTheme.id === Number(theme.id),
  );
  if (tmpTheme === undefined) throw new Error('theme is undefined');
  tmpTheme.is_favorite = !tmpTheme.is_favorite;
  await store.commit('themeList/updateTheme', tmpTheme);
  emitter.emit('theme-updated', tmpTheme);
};

const dismissAgentsRecommendation = async () => {
  await api.patchUser({ agents_recommendation_dismissed: true });
  showAgentsRecommendation.value = false;
};

watch(
  () => userInfo.value,
  userInfo => {
    if (!userInfo) return;
    showAgentsRecommendation.value =
      !userInfo.agents_recommendation_dismissed_at;
  },
  { immediate: true },
);
</script>

<template>
  <div class="o-anews-home" v-if="isLoaded">
    <div v-if="hasThemes">
      <Insights />
      <div class="o-home-body">
        <div class="o-home-main">
          <PersonalNewsPreviewMultipleExecution />
          <div class="o-spacing"></div>
          <div class="o-theme-favorite-board" v-if="showThemeSearchBoard">
            <ThemeFavoriteBoard></ThemeFavoriteBoard>
          </div>
          <div class="o-spacing" v-if="showThemeSearchBoard"></div>
          <div class="o-theme-favorite-board" v-if="showThemeFavoriteBoard">
            <ThemeFavoriteBoard
              @toggle-favorite="toggleFavorite"
            ></ThemeFavoriteBoard>
          </div>
          <div class="o-spacing" v-if="showThemeFavoriteBoard"></div>
          <div class="o-picked-up-feeds" v-if="hasFavoriteThemes">
            <PickedUpFeeds></PickedUpFeeds>
          </div>
          <div v-if="feedType !== 'foreign'">
            <div class="spacing-16"></div>
            <IndustryNewsPreview></IndustryNewsPreview>
          </div>
        </div>
        <div class="o-team-comments" v-if="hasThemes" ref="teamCommentsColumn">
          <div
            class="o-team-comments-column-content"
            ref="teamCommentsContent"
            style="position: absolute; top: 0"
          >
            <template v-if="showAgentsRecommendation">
              <AgentsRecommendation
                @dismiss-agents-recommendation="dismissAgentsRecommendation"
              />
            </template>
            <div
              class="featured-articles"
              v-if="featuredArticles"
              :ref="setFeaturedArticlesRef"
            >
              <FeaturedArticleList
                page-name="home"
                feature="team_feature_articles"
                :articles="visibleFeaturedArticles"
                link-to="/team/featured_articles"
                :contents-context="{
                  event_name: 'team_feature_articles',
                  parent_request_id: featuredArticlesRequestId,
                }"
              ></FeaturedArticleList>
              <MobileAppInfo></MobileAppInfo>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="o-has-no-theme" v-else>
      <div class="o-explanation">
        <div class="o-sentence">
          <div class="o-title c-title-bold--l">まずはテーマを作成</div>
          <div class="o-sentence1 o-text c-text-regular--m">
            毎日受け取りたいニュースのテーマを作成しましょう！
          </div>
          <div class="o-sentence2 o-text c-text-regular--m">
            どのようなテーマを作ったらいいのか悩んだときは、<br />
            お気軽に<a
              class="o-help-text"
              :href="'https://anews-stockmark.zendesk.com/hc/ja'"
              target="_blank"
              rel="noopener noreferrer"
              >ヘルプ</a
            >からカスタマーサポートにご相談ください。
          </div>
          <div class="o-footer">
            <router-link :to="{ name: 'themeCreate' }">
              <button class="c-btn c-btn--auto c-btn--AnewsPrimary">
                テーマを作成する
              </button>
            </router-link>
          </div>
        </div>
        <img class="o-no-theme" src="@/assets/state-empty-theme.png" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.o-anews-home {
  .o-home-body {
    display: flex;
    flex-direction: row;
    width: max-content;
    margin: 16px auto 0 auto;
    .o-home-main {
      width: 616px;
      margin-right: 16px;
      margin-left: 16px;
      border-radius: 4px;
      .o-theme-favorite-board {
        .o-spacing {
          margin-top: 68px;
        }
      }
      .o-spacing {
        margin-top: 16px;
      }
    }
  }
  .o-has-no-theme {
    width: 940px;
    margin: auto;
    padding-top: 24px;
    .o-explanation {
      width: 940px;
      height: 280px;
      background: #ffffff;
      border: 1px solid $color-gray400;
      box-sizing: border-box;
      border-radius: 4px;
      display: flex;
      flex-direction: row;
      align-items: center;
      .o-sentence {
        padding: 44px 88px 58px 58px;
        .o-sentence1 {
          margin-top: 12px;
        }
        .o-sentence2 {
          margin-top: 12px;
          .o-help-text {
            color: $color-green800;
            cursor: pointer;
            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
      .o-footer {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-top: 20px;
      }
      .o-no-theme {
        width: 414px;
        height: 280px;
        border-radius: 0 4px 4px 0;
      }
    }
  }
}
.o-team-comments {
  position: relative;
  width: 280px;
  margin-right: 16px;
  .featured-articles {
    padding-bottom: 80px;
  }
  .o-team-comments-column-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }
}
.o-has-no-comments {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px;
  width: 266px;
  height: 242px;
  background: #ffffff;
  .o-description {
    color: $color-gray800;
  }
  .has-no-comments {
    height: 200px;
    width: 200px;
    margin-top: 10px;
  }
}
.o-read-more {
  font-weight: normal;
  padding: 6px 8px;
  color: $color-green600;
  border-radius: 4px;
  text-align: center;
  margin: 4px 8px 0 0;
  &:hover {
    background-color: $color-gray200;
  }
}

.o-read-more {
  margin-left: 198px;
  &:hover {
    background-color: $color-gray400;
  }
}

button {
  outline: none;
}
.m-mobile-app-info {
  margin-bottom: 80px;
}
</style>
